// Display helpers for affiliate statements. Periods are half-open UTC calendar ranges, so
// every date here is formatted and computed in UTC (a viewer west of Greenwich must never
// see the previous day).

export type StatementCadence = "monthly" | "quarterly"

/** "$1,234.50", with a leading minus for negatives ("-$12.00"). */
export function formatCents(cents: number): string {
  const abs = Math.abs(cents)
  const dollars = (abs / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `${cents < 0 ? "-" : ""}$${dollars}`
}

/** "Oct 1, 2026", in UTC. */
export function formatDateUtc(value: string | Date): string {
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  })
}

/** "Oct 1, 2026, 3:04 PM UTC". */
export function formatDateTimeUtc(value: string | Date): string {
  return `${new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  })} UTC`
}

/** "Q4 2026" for a quarter, "October 2026" for a month, else "Oct 1, 2026 to Dec 31, 2026". */
export function periodLabel(startIso: string, endIso: string): string {
  const start = new Date(startIso)
  const end = new Date(endIso)
  const monthSpan =
    (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + (end.getUTCMonth() - start.getUTCMonth())
  if (monthSpan === 3 && start.getUTCMonth() % 3 === 0) {
    return `Q${start.getUTCMonth() / 3 + 1} ${start.getUTCFullYear()}`
  }
  if (monthSpan === 1) {
    const month = start.toLocaleString("en-US", { month: "long", timeZone: "UTC" })
    return `${month} ${start.getUTCFullYear()}`
  }
  return `${formatDateUtc(start)} to ${formatDateUtc(new Date(end.getTime() - 1))}`
}

/** A `<input type="date">` value ("2026-10-02") as UTC midnight, the API's datetime shape. */
export function dateInputToUtcIso(value: string): string | null {
  return value ? `${value}T00:00:00.000Z` : null
}

/** The UTC calendar date of an ISO timestamp, as a `<input type="date">` value. */
export function isoToDateInput(value: string | null): string {
  return value ? new Date(value).toISOString().slice(0, 10) : ""
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Split a recipients box on newlines, commas and semicolons, dropping blanks and repeats. */
export function parseRecipients(text: string): { emails: string[]; invalid: string[] } {
  const seen = new Set<string>()
  const emails: string[] = []
  const invalid: string[] = []
  for (const raw of text.split(/[\n,;]+/)) {
    const email = raw.trim()
    if (!email) continue
    const key = email.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    if (EMAIL.test(email)) emails.push(email)
    else invalid.push(email)
  }
  return { emails, invalid }
}

export type StatementStatus = "draft" | "sent" | "paid"

export const STATEMENT_STATUS_CLASS: Record<StatementStatus, string> = {
  draft: "bg-gray-800 text-secondary",
  sent: "bg-blue-900/30 text-blue-400",
  paid: "bg-green-900/30 text-green-400",
}
